import styled from 'styled-components';
import LogoGame2Value from '../assets/images/logo.svg';
import IconSelectArrow from '../assets/images/icon-select-arrow-small.svg';

import {useTranslation} from 'react-i18next';
import {updateLanguage} from '../utils/api';

interface HeaderProps {
  user?: string;
  clientLogo?: string;
  clientName?: string;
  sessionToken?: string;
}

export default function Header({
  user,
  clientLogo,
  clientName,
  sessionToken,
}: HeaderProps) {
  const {t, i18n} = useTranslation();

  const handleLanguageChange = async (e: any) => {
    i18n.changeLanguage(e.target.value);

    if (sessionToken) {
      const response = await updateLanguage(e.target.value, sessionToken);
      console.log(response);
    }
  };

  return (
    <Container>
      <Inner>
        <Logos>
          <a
            href="https://game2value.it/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Logo src={LogoGame2Value} alt="Game2Value Logo" />
          </a>
          {clientLogo && (
            <>
              <LogosDivider />
              <LogoClient
                src={`${process.env.REACT_APP_BASE_URL}/uploads/images/customer/${clientLogo}`}
                alt={clientName ? clientName : 'Logo'}
              />
            </>
          )}
        </Logos>
        <Nav>
          {user && (
            <UserBox>
              <span>{user}</span>
              <svg
                id="single-user"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
              >
                <path
                  id="Tracciato_84"
                  data-name="Tracciato 84"
                  d="M20,20v-.75A4.25,4.25,0,0,0,15.75,15H8.25A4.25,4.25,0,0,0,4,19.25V20"
                  fill="none"
                  stroke="#323232"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.5"
                />
                <circle
                  id="Ellisse_4"
                  data-name="Ellisse 4"
                  cx="4"
                  cy="4"
                  r="4"
                  transform="translate(8 3)"
                  fill="none"
                  stroke="#323232"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.5"
                />
                <rect
                  id="Rettangolo_41"
                  data-name="Rettangolo 41"
                  width="24"
                  height="24"
                  fill="none"
                />
              </svg>
            </UserBox>
          )}
          <SelectContainer>
            <select onChange={handleLanguageChange} value={i18n.language}>
              <option value="it">{t('language.it')}</option>
              <option value="en">{t('language.en')}</option>
              <option value="es">{t('language.es')}</option>
              <option value="pl">{t('language.pl')}</option>
            </select>
          </SelectContainer>
        </Nav>
      </Inner>
    </Container>
  );
}

const Container = styled.header`
  padding: 10px 20px;
`;

const Inner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: ${(props) => props.theme.sizes.mw};
  margin: 0 auto;
  padding: 0.5rem 0;
`;

const Logos = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-right: 0.5rem;
`;

const LogosDivider = styled.span`
  display: block;
  width: 1px;
  height: 30px;
  background-color: ${(props) => props.theme.colors.gray300};
  margin: 0 1.5rem;

  @media ${(props) => props.theme.breakpoints.phone} {
    margin: 0 0.5rem;
  }
`;

const Logo = styled.img`
  display: block;
  max-height: 40px;

  @media ${(props) => props.theme.breakpoints.tablet} {
    max-height: 1.5rem;
  }

  @media ${(props) => props.theme.breakpoints.phone} {
    max-height: 24px;
    max-width: 23vw;
  }
`;

const LogoClient = styled.img`
  display: block;
  max-height: 40px;

  @media ${(props) => props.theme.breakpoints.tablet} {
    max-height: 1.5rem;
  }

  @media ${(props) => props.theme.breakpoints.phone} {
    max-height: 24px;
    max-width: 23vw;
  }
`;

const Nav = styled.div`
  display: flex;
  align-items: center;
`;

const UserBox = styled.div`
  display: flex;
  align-items: center;
  margin-right: 1.5rem;

  span {
    display: inline-block;
    margin-right: 0.5rem;
    text-align: right;

    @media ${(props) => props.theme.breakpoints.tablet} {
      max-width: 150px;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }

  svg {
    flex-shrink: 0;
  }

  @media ${(props) => props.theme.breakpoints.phone} {
    display: none;
  }
`;

const SelectContainer = styled.div`
  position: relative;
  cursor: pointer;

  &::after {
    content: '';
    pointer-events: none;
    width: 9px;
    height: 4px;
    background-image: url(${IconSelectArrow});
    background-position: center;
    background-size: 100%;
    background-repeat: no-repeat;
    position: absolute;
    top: 50%;
    right: 14px;
    transform: translateY(-50%);
  }

  select {
    appearance: none;
    padding: 10px 28px 10px 14px;
    border-radius: 10px;
    border: none;
    background-color: ${(props) => props.theme.colors.gray200};
    font-size: 1rem;
    font-family: inherit;
    cursor: pointer;
  }
`;
